import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import { entities, modules } from "byzantine";
import type { Invoice } from "byzantine/src/dbbl/businessLogic/entities/invoices/types";
import type { InvoicePreviewPage } from "./types";
import { useInvoiceUploadConfig } from "./useInvoiceUploadConfig";
import useInvoiceUpload from "./useInvoiceUpload";

type InvoiceDeliveryOptionDisplay = {
  arrival: string;
  fee: string;
  label: string;
};

type InvoiceFlowContextValue = {
  uploadedFileName: string | null;
  previewUrl: string | null;
  previewPages: InvoicePreviewPage[];
  previewMimeType: string | null;
  completedCount: number;
  requestDone: boolean | null;
  requiresManualEntry: boolean;
  invoice: Invoice | null;
  savedRecipientId: API.RecipientId | null;
  deliveryOptionDisplay: InvoiceDeliveryOptionDisplay | null;
  setSavedRecipientId: (recipientId: API.RecipientId | null) => void;
  setDeliveryOptionDisplay: (
    deliveryOptionDisplay: InvoiceDeliveryOptionDisplay | null
  ) => void;
  handleSendRequest: (file: File) => void;
  handleFileNameChange: (fileName: string | null) => void;
  clearUploadedFile: () => void;
};

const InvoiceFlowContext = createContext<InvoiceFlowContextValue | null>(null);

export const useInvoiceFlow = () => {
  const context = useContext(InvoiceFlowContext);
  if (!context) {
    throw new Error(
      "useInvoiceFlow must be used within an InvoiceFlowProvider"
    );
  }
  return context;
};

export const InvoiceFlowProvider = ({ children }: { children: ReactNode }) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const uploadConfig = useInvoiceUploadConfig();
  const achPaymentForm = modules.achPayments.ACHPaymentForm.useForm();
  const recipientForm = entities.recipients.ACHPaymentRecipientForm.useForm();
  const {
    resetForm: resetAchPaymentForm,
    setFieldValue: setAchPaymentFieldValue,
  } = achPaymentForm;
  const {
    setFieldValue: setRecipientFieldValue,
    resetForm: resetRecipientForm,
  } = recipientForm;
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [requestDone, setRequestDone] = useState<boolean | null>(null);
  const [savedRecipientId, setSavedRecipientId] =
    useState<API.RecipientId | null>(null);
  const [deliveryOptionDisplay, setDeliveryOptionDisplay] =
    useState<InvoiceDeliveryOptionDisplay | null>(null);

  const { upload, abort } = useInvoiceUpload();
  const { poll, stop } = entities.invoices.usePollInvoice();

  const completedCount = requestDone
    ? entities.invoices.INVOICE_SCANNING_ROW_COUNT
    : entities.invoices.getInvoiceScanningCompletedRowCount(
        invoice?.status ?? null
      );
  const requiresManualEntry =
    requestDone === true &&
    entities.invoices.requiresInvoicePayManualEntry(invoice);

  // decides how to render the local preview: an image shows immediately, while
  // a PDF has to wait on a spinner until the backend renders its pages
  const previewMimeType = uploadedFile?.type ?? null;

  const previewPages = useMemo<InvoicePreviewPage[]>(
    () =>
      (invoice?.pages ?? []).map((page) => ({
        url: page.image_url,
        widthPx: page.width_px,
        heightPx: page.height_px,
      })),
    [invoice]
  );

  const resetInvoiceFlowForms = useCallback(() => {
    resetAchPaymentForm();
    resetRecipientForm();
  }, [resetAchPaymentForm, resetRecipientForm]);

  // build (and clean up) an object URL for the uploaded file preview
  useEffect(() => {
    if (!uploadedFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(uploadedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [uploadedFile]);

  // upload the file, then poll the invoice until the extraction pipeline
  // reaches a terminal status. the checklist ticks off the backend's real
  // progress rather than a timer.
  useEffect(() => {
    setSavedRecipientId(null);
    setRecipientFieldValue("selectedRecipientId", "");
    setInvoice(null);
    setRequestDone(uploadedFile ? false : null);
    setDeliveryOptionDisplay(null);
    setAchPaymentFieldValue("deliverySpeedManuallySelected", false);

    if (!uploadedFile) return undefined;

    let cancelled = false;

    // put the flow back to a bare uploader so the user can try again; there is
    // no retry affordance once a file is attached
    const failAndReset = (text: string) => {
      if (cancelled) return;
      setUploadedFile(null);
      setUploadedFileName(null);
      sendNotification({ type: "negative", text });
    };

    (async () => {
      let uploaded;
      try {
        uploaded = await upload(uploadedFile);
      } catch {
        failAndReset(l10n.getString("invoice-upload-error-failed"));
        return;
      }
      // undefined means the upload was aborted, which is already a state change
      // someone else asked for
      if (!uploaded || cancelled) return;

      const finalInvoice = await poll(uploaded.uuid, {
        // surface each status change so the checklist advances mid-pipeline
        onUpdate: (next) => {
          if (cancelled) return;

          setInvoice(next);
          if (entities.invoices.canContinueInvoicePayFlow(next)) {
            setRequestDone(true);
          }
        },
      });

      if (cancelled) return;
      if (!entities.invoices.canContinueInvoicePayFlow(finalInvoice)) {
        failAndReset(l10n.getString("invoice-upload-error-processing"));
        return;
      }

      setInvoice(finalInvoice);
      setRequestDone(true);
    })();

    return () => {
      cancelled = true;
      abort();
      stop();
    };
  }, [
    uploadedFile,
    setAchPaymentFieldValue,
    setRecipientFieldValue,
    upload,
    abort,
    poll,
    stop,
    sendNotification,
    l10n,
  ]);

  const value = useMemo<InvoiceFlowContextValue>(() => {
    const handleSendRequest = (file: File) => {
      if (!uploadConfig.allowed_mime_types.includes(file.type)) {
        sendNotification({
          type: "negative",
          text: l10n.getString("invoice-upload-error-invalid-type"),
        });
        throw new Error("Invalid file type");
      }
      if (file.size > uploadConfig.max_size_bytes) {
        const maxSizeMb = Math.round(
          uploadConfig.max_size_bytes / (1024 * 1024)
        );
        sendNotification({
          type: "negative",
          text: l10n.getString("invoice-upload-error-too-large", { maxSizeMb }),
        });
        throw new Error("File too large");
      }

      resetInvoiceFlowForms();
      setUploadedFile(file);
    };

    const handleFileNameChange = (fileName: string | null) => {
      setUploadedFileName(fileName);
      // file removed — the effect resets animation/request state
      if (!fileName) {
        resetInvoiceFlowForms();
        setUploadedFile(null);
      }
    };

    const clearUploadedFile = () => {
      resetInvoiceFlowForms();
      setUploadedFile(null);
      setUploadedFileName(null);
    };

    return {
      uploadedFileName,
      previewUrl,
      previewPages,
      previewMimeType,
      completedCount,
      requestDone,
      requiresManualEntry,
      invoice,
      savedRecipientId,
      deliveryOptionDisplay,
      setSavedRecipientId,
      setDeliveryOptionDisplay,
      handleSendRequest,
      handleFileNameChange,
      clearUploadedFile,
    };
  }, [
    uploadedFileName,
    previewUrl,
    previewPages,
    previewMimeType,
    completedCount,
    requestDone,
    requiresManualEntry,
    invoice,
    savedRecipientId,
    deliveryOptionDisplay,
    resetInvoiceFlowForms,
    l10n,
    sendNotification,
    uploadConfig,
  ]);

  return (
    <InvoiceFlowContext.Provider value={value}>
      {children}
    </InvoiceFlowContext.Provider>
  );
};
